import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Drawer from '@mui/material/Drawer';
import { Close } from '@mui/icons-material';
import styled from '@emotion/styled';
import { settingsTheme as t } from '../../theme';
import { Instructor, InstructorDraft, InstructorStatus } from '../data/instructors';
import { SaveResult } from '../hooks/useInstructors';
import { instructorFormSchema, InstructorFormValues } from '../schemas/instructor-form-schema';
import Button from './Button';
import FormField from './form/FormField';
import TextInput from './form/TextInput';
import SegmentedControl from './form/SegmentedControl';

export interface InstructorFormLabels {
  title: string;
  subtitle: string;
  close: string;
  name: string;
  namePlaceholder: string;
  email: string;
  emailPlaceholder: string;
  phone: string;
  phonePlaceholder: string;
  organization: string;
  organizationPlaceholder: string;
  status: string;
  statusActive: string;
  statusInactive: string;
  cancel: string;
  save: string;
}

interface InstructorFormDrawerProps {
  open: boolean;
  /** Instructor en edición, o `null` para alta. */
  editing: Instructor | null;
  labels: InstructorFormLabels;
  onClose: () => void;
  onSubmit: (draft: InstructorDraft) => Promise<SaveResult>;
}

const emptyValues: InstructorFormValues = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  status: 'active',
};

const toFormValues = (instructor: Instructor | null): InstructorFormValues =>
  instructor
    ? {
        name: instructor.name,
        email: instructor.email,
        phone: instructor.phone,
        organization: instructor.organization,
        status: instructor.status,
      }
    : emptyValues;

const Panel = styled.div({
  width: 480,
  maxWidth: '92vw',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Head = styled.div({
  flex: 'none',
  padding: '22px 26px',
  borderBottom: `1px solid ${t.color.rowBorder}`,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const HeadTitle = styled.div({
  fontFamily: t.font,
  fontSize: 18,
  fontWeight: 600,
  color: t.color.title,
});

const HeadSubtitle = styled.div({
  fontFamily: t.font,
  fontSize: 13,
  color: t.color.muted,
  marginTop: 3,
});

const CloseButton = styled.button({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: t.color.muted,
  display: 'inline-flex',
  '& .MuiSvgIcon-root': { fontSize: 24 },
});

const Body = styled.form({
  flex: 1,
  overflowY: 'auto',
  padding: '24px 26px',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
});

const FieldPair = styled.div({
  display: 'flex',
  gap: 14,
  '& > *': { flex: 1, minWidth: 0 },
});

const Footer = styled.div({
  flex: 'none',
  borderTop: `1px solid ${t.color.rowBorder}`,
  padding: '18px 26px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
});

/**
 * Organismo: panel lateral de alta y edición de un instructor.
 *
 * La validación corre con react-hook-form + zod (`instructorFormSchema`).
 * El correo y el teléfono validan formato; los errores se muestran bajo cada
 * campo y marcan el borde en rojo. El tipo de instructor no se pide: todos son
 * externos y se fija en el envío.
 */
const InstructorFormDrawer = ({
  open,
  editing,
  labels,
  onClose,
  onSubmit,
}: InstructorFormDrawerProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<InstructorFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: zodResolver(instructorFormSchema),
    defaultValues: emptyValues,
  });

  const [submitting, setSubmitting] = useState(false);

  // Reinicia el formulario al abrir, según se trate de alta o edición.
  useEffect(() => {
    if (open) reset(toFormValues(editing));
    // Solo reaccionamos a la apertura; `editing` se captura en ese instante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (values: InstructorFormValues) => {
    setSubmitting(true);
    // Construimos el draft explícitamente (con `strict: false` la inferencia de
    // zod marca los campos como opcionales y el spread no encaja en InstructorDraft).
    // Todos los instructores son externos; al editar se conserva su tipo.
    const result = await onSubmit({
      name: values.name,
      email: values.email,
      phone: values.phone,
      organization: values.organization,
      status: values.status,
      type: editing?.type ?? 'external',
    });
    setSubmitting(false);
    // El correo duplicado (409) se muestra como error del propio campo.
    if (result?.emailTaken) {
      setError('email', { type: 'server', message: result.message });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { boxShadow: '-8px 0 30px rgba(16, 24, 40, 0.18)' } }}
    >
      <Panel>
        <Head>
          <div>
            <HeadTitle>{labels.title}</HeadTitle>
            <HeadSubtitle>{labels.subtitle}</HeadSubtitle>
          </div>
          <CloseButton type="button" onClick={onClose} aria-label={labels.close}>
            <Close />
          </CloseButton>
        </Head>

        <Body id="instructor-form" onSubmit={handleSubmit(submit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FormField label={labels.name} error={errors.name?.message}>
                <TextInput
                  value={field.value}
                  placeholder={labels.namePlaceholder}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  invalid={!!errors.name}
                />
              </FormField>
            )}
          />

          <FieldPair>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <FormField label={labels.email} error={errors.email?.message}>
                  <TextInput
                    value={field.value}
                    placeholder={labels.emailPlaceholder}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    invalid={!!errors.email}
                  />
                </FormField>
              )}
            />
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <FormField label={labels.phone} error={errors.phone?.message}>
                  <TextInput
                    value={field.value}
                    placeholder={labels.phonePlaceholder}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    invalid={!!errors.phone}
                  />
                </FormField>
              )}
            />
          </FieldPair>

          <Controller
            name="organization"
            control={control}
            render={({ field }) => (
              <FormField label={labels.organization} error={errors.organization?.message}>
                <TextInput
                  value={field.value}
                  placeholder={labels.organizationPlaceholder}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              </FormField>
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormField label={labels.status}>
                <SegmentedControl<InstructorStatus>
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    { value: 'active', label: labels.statusActive, activeColor: t.color.statusActive },
                    { value: 'inactive', label: labels.statusInactive, activeColor: t.color.danger },
                  ]}
                />
              </FormField>
            )}
          />
        </Body>

        <Footer>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {labels.cancel}
          </Button>
          <Button type="submit" form="instructor-form" variant="primary" disabled={submitting}>
            {labels.save}
          </Button>
        </Footer>
      </Panel>
    </Drawer>
  );
};

export default InstructorFormDrawer;
